import {Card, CardContent} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import {useEffect, useRef, useState} from 'react';
import {COLORS, EXPORTING} from '../../../utils/constants';
import {groupBy} from '../../../utils/groupBy';
import {AGE_GROUP_KEYS, latestPerUoa, meanOf, percentageOf} from '../../../utils/wia';
import {roundTo} from '../../../utils/round';

interface IAgeGroupsData {
    submissions: any[];
    uoa: string | number
}

interface IGroupData {
    '65_75': number;
    '75_85': number;
    'over_85': number;
}

const countGroups = (submissions: any[]): IGroupData => ({
    '65_75': submissions.reduce((a, s) => a + (s[AGE_GROUP_KEYS[0]] || 0), 0),
    '75_85': submissions.reduce((a, s) => a + (s[AGE_GROUP_KEYS[1]] || 0), 0),
    'over_85': submissions.reduce((a, s) => a + (s[AGE_GROUP_KEYS[2]] || 0), 0),
});

const totalOf = (counts: IGroupData): number =>
    counts['65_75'] + counts['75_85'] + counts['over_85'];

const toPercentages = (counts: IGroupData): IGroupData => ({
    '65_75': percentageOf(counts['65_75'], totalOf(counts)),
    '75_85': percentageOf(counts['75_85'], totalOf(counts)),
    'over_85': percentageOf(counts['over_85'], totalOf(counts)),
});

const AgeGroups = ({submissions, uoa}: IAgeGroupsData) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const [options, setOptions] = useState<Highcharts.Options>();

    const calculateSubmissions = () => {
        const latest = latestPerUoa(submissions);

        const currentSubmissions = toPercentages(
            countGroups(latest.filter((submission: any) => submission.uoa_parent_id === uoa))
        );

        const projects: IGroupData[] = Object.values(groupBy(latest, 'uoa_parent_id'))
            .map((project: any) => countGroups(project))
            .filter((counts: IGroupData) => totalOf(counts) > 0)
            .map(toPercentages);

        const allSubmissions: IGroupData = {
            '65_75': meanOf(projects.map(project => project['65_75'])),
            '75_85': meanOf(projects.map(project => project['75_85'])),
            'over_85': meanOf(projects.map(project => project['over_85'])),
        };

        const dashboardOptions: any = {
            chart: {
                height: 400,
                type: 'column',
                style: {
                    fontFamily: 'Rubik, sans-serif'
                },
                backgroundColor: '#fafafa'
            },
            title: {
                text: 'Composizione beneficiari attesi - fasce di età',
                align: 'left'
            },
            xAxis: {
                categories: ['Il mio progetto', 'Media dei progetti']
            },
            yAxis: {
                min: 0,
                max: 100,
                labels: {
                    format: '{value}%'
                },
                title: {
                    text: ''
                },
                stackLabels: {
                    enabled: false,
                },
            },
            tooltip: {
                valueSuffix: '%'
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    stacking: 'percent',
                }
            },
            series: [
                {
                    name: '% di beneficiari anziani attesi over 85',
                    color: COLORS.yellow,
                    data: [
                        roundTo(currentSubmissions.over_85),
                        roundTo(allSubmissions.over_85)
                    ]
                },
                {
                    name: '% di beneficiari anziani attesi in età 75-85',
                    color: COLORS.red,
                    data: [
                        roundTo(currentSubmissions['75_85']),
                        roundTo(allSubmissions['75_85'])
                    ]
                },
                {
                    name: '% di beneficiari anziani attesi in età 65-75',
                    color: COLORS.blue,
                    data: [
                        roundTo(currentSubmissions['65_75']),
                        roundTo(allSubmissions['65_75'])
                    ]
                }
            ],
            exporting: EXPORTING
        };

        setOptions(dashboardOptions);
    };

    useEffect(() => {
        calculateSubmissions();
    }, []);

    return (
        <Card>
            <CardContent>
                {
                    options && (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            ref={chartComponentRef}
                        />
                    )
                }
            </CardContent>
        </Card>
    );
};

export default AgeGroups;
